"use client"

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react"
import { trackPodcastPlay } from "@/lib/analytics"

interface AudioTrack {
  id: string
  title: string
  url: string
  source?: "podcast" | "summary"
  meta?: {
    bookId?: string
    bookSlug?: string
  }
}

interface AudioPlayerContextValue {
  currentTrack: AudioTrack | null
  isPlaying: boolean
  isBuffering: boolean
  currentTime: number
  duration: number
  volume: number
  isMuted: boolean
  playbackRate: number
  isExpanded: boolean
  playTrack: (track: AudioTrack) => Promise<void>
  togglePlayPause: () => Promise<void>
  stop: () => void
  seek: (time: number) => void
  seekBy: (deltaSeconds: number) => void
  setVolume: (value: number) => void
  toggleMute: () => void
  setPlaybackRate: (value: number) => void
  openExpanded: () => void
  closeExpanded: () => void
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | undefined>(undefined)

export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const currentTrackRef = useRef<AudioTrack | null>(null)
  const podcastMilestonesRef = useRef({
    trackId: "" as string,
    announcedPlay: false,
    m25: false,
    m50: false,
    m75: false,
  })
  const [currentTrack, setCurrentTrack] = useState<AudioTrack | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isBuffering, setIsBuffering] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolumeState] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [playbackRate, setPlaybackRateState] = useState(1)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    currentTrackRef.current = currentTrack
  }, [currentTrack])

  const normalizeTrackUrl = useCallback((url: string) => {
    if (typeof window === "undefined") return url
    try {
      return new URL(url, window.location.origin).toString()
    } catch {
      return url
    }
  }, [])

  // Initialize audio element once
  useEffect(() => {
    const audio = new Audio()
    audio.preload = "auto"
    // Volume and playbackRate are set by separate sync effects below
    audioRef.current = audio

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
      const track = currentTrackRef.current
      if (!track || track.source !== "podcast" || !track.meta?.bookId || !track.meta?.bookSlug) return
      const d = audio.duration
      if (!d || !isFinite(d) || d <= 0) return
      const ratio = audio.currentTime / d
      const st = podcastMilestonesRef.current
      if (st.trackId !== track.id) return
      if (ratio >= 0.25 && !st.m25) {
        st.m25 = true
        trackPodcastPlay({
          action: "25%",
          episode_id: track.meta.bookId,
          episode_title: track.title.slice(0, 200),
          book_slug: track.meta.bookSlug,
        })
      }
      if (ratio >= 0.5 && !st.m50) {
        st.m50 = true
        trackPodcastPlay({
          action: "50%",
          episode_id: track.meta.bookId,
          episode_title: track.title.slice(0, 200),
          book_slug: track.meta.bookSlug,
        })
      }
      if (ratio >= 0.75 && !st.m75) {
        st.m75 = true
        trackPodcastPlay({
          action: "75%",
          episode_id: track.meta.bookId,
          episode_title: track.title.slice(0, 200),
          book_slug: track.meta.bookSlug,
        })
      }
    }
    const handleLoadedMetadata = () => setDuration(audio.duration || 0)
    const handlePlay = () => {
      setIsPlaying(true)
      setIsBuffering(false)
      const track = currentTrackRef.current
      if (track?.source === "podcast" && track.meta?.bookId && track.meta?.bookSlug) {
        const st = podcastMilestonesRef.current
        if (st.trackId === track.id && !st.announcedPlay) {
          st.announcedPlay = true
          trackPodcastPlay({
            action: "play",
            episode_id: track.meta.bookId,
            episode_title: track.title.slice(0, 200),
            book_slug: track.meta.bookSlug,
          })
        }
      }
    }
    const handlePause = () => {
      setIsPlaying(false)
      const track = currentTrackRef.current
      if (track?.source === "podcast" && track.meta?.bookId && track.meta?.bookSlug) {
        trackPodcastPlay({
          action: "paused",
          episode_id: track.meta.bookId,
          episode_title: track.title.slice(0, 200),
          book_slug: track.meta.bookSlug,
        })
      }
    }
    const handleWaiting = () => setIsBuffering(true)
    const handlePlaying = () => setIsBuffering(false)
    const handleEnded = () => {
      setIsPlaying(false)
      setIsBuffering(false)
      const track = currentTrackRef.current
      if (track?.source === "podcast" && track.meta?.bookId && track.meta?.bookSlug) {
        trackPodcastPlay({
          action: "completed",
          episode_id: track.meta.bookId,
          episode_title: track.title.slice(0, 200),
          book_slug: track.meta.bookSlug,
        })
      }
    }

    audio.addEventListener("timeupdate", handleTimeUpdate)
    audio.addEventListener("loadedmetadata", handleLoadedMetadata)
    audio.addEventListener("play", handlePlay)
    audio.addEventListener("pause", handlePause)
    audio.addEventListener("waiting", handleWaiting)
    audio.addEventListener("playing", handlePlaying)
    audio.addEventListener("ended", handleEnded)

    return () => {
      audio.pause()
      audio.src = ""
      audio.removeEventListener("timeupdate", handleTimeUpdate)
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata)
      audio.removeEventListener("play", handlePlay)
      audio.removeEventListener("pause", handlePause)
      audio.removeEventListener("waiting", handleWaiting)
      audio.removeEventListener("playing", handlePlaying)
      audio.removeEventListener("ended", handleEnded)
    }
  }, [])

  // Keep element volume/mute in sync
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.volume = isMuted ? 0 : volume
  }, [volume, isMuted])

  // Keep playback rate in sync
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.playbackRate = playbackRate
  }, [playbackRate])

  const playTrack = useCallback(async (track: AudioTrack) => {
    const audio = audioRef.current
    if (!audio) return

    const nextUrl = normalizeTrackUrl(track.url)
    if (currentTrack?.id !== track.id || audio.src !== nextUrl) {
      audio.src = nextUrl
      audio.load()
      setCurrentTrack(track)
      currentTrackRef.current = track
      setCurrentTime(0)
      podcastMilestonesRef.current = {
        trackId: track.id,
        announcedPlay: false,
        m25: false,
        m50: false,
        m75: false,
      }
    } else {
      currentTrackRef.current = track
    }

    setIsBuffering(true)
    try {
      await audio.play()
    } catch (error) {
      console.error("Audio playback failed:", error)
      setIsBuffering(false)
      throw error
    }
  }, [currentTrack, normalizeTrackUrl])

  const togglePlayPause = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      setIsBuffering(true)
      try {
        await audio.play()
      } catch (error) {
        console.error("Failed to resume audio:", error)
        setIsBuffering(false)
      }
    } else {
      audio.pause()
    }
  }, [])

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    audio.pause()
    audio.currentTime = 0
    setIsPlaying(false)
    setCurrentTime(0)
    setCurrentTrack(null)
    currentTrackRef.current = null
  }, [])

  const seek = useCallback((time: number) => {
    const audio = audioRef.current
    if (!audio || !isFinite(time)) return
    audio.currentTime = Math.max(0, Math.min(audio.duration || 0, time))
    setCurrentTime(audio.currentTime)
  }, [])

  const seekBy = useCallback((deltaSeconds: number) => {
    const audio = audioRef.current
    if (!audio) return
    const nextTime = Math.max(0, Math.min(audio.duration || 0, audio.currentTime + deltaSeconds))
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }, [])

  const setVolume = useCallback((value: number) => {
    const audio = audioRef.current
    if (!audio) return

    const nextVolume = Math.max(0, Math.min(1, value))
    audio.volume = nextVolume
    setVolumeState(nextVolume)
    setIsMuted(nextVolume === 0)
  }, [])

  const toggleMute = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isMuted) {
      audio.volume = volume
      setIsMuted(false)
    } else {
      audio.volume = 0
      setIsMuted(true)
    }
  }, [isMuted, volume])

  const setPlaybackRate = useCallback((value: number) => {
    const audio = audioRef.current
    if (!audio) return
    const next = Math.max(0.5, Math.min(2, value))
    audio.playbackRate = next
    setPlaybackRateState(next)
  }, [])

  const openExpanded = useCallback(() => {
    if (currentTrack) {
      setIsExpanded(true)
    }
  }, [currentTrack])

  const closeExpanded = useCallback(() => {
    setIsExpanded(false)
  }, [])

  const value: AudioPlayerContextValue = {
    currentTrack,
    isPlaying,
    isBuffering,
    currentTime,
    duration,
    volume,
    isMuted,
    playbackRate,
    isExpanded,
    playTrack,
    togglePlayPause,
    stop,
    seek,
    seekBy,
    setVolume,
    toggleMute,
    setPlaybackRate,
    openExpanded,
    closeExpanded,
  }

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  )
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext)
  if (!context) {
    throw new Error("useAudioPlayer must be used within an AudioPlayerProvider")
  }
  return context
}

